import { useRef, useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB — matches the server limit

/**
 * Generic image picker + immediate upload. On file select it validates (image,
 * ≤5MB), calls the provided `uploader` (which posts to an upload endpoint), and
 * hands the returned CDN url back via `onChange`. The caller persists that url
 * in its own JSON (ProductRegionConfig images, bundle session image, etc.).
 * Mirrors the professionals file-upload UX (preview, 5MB guard, image-only).
 */
export default function ImageUploadField({
  label,
  value,
  onChange,
  uploader,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  uploader: (file: File) => Promise<{ url: string }>;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Only image files are allowed", variant: "destructive" });
      return;
    }
    if (file.size > MAX_BYTES) {
      toast({ title: "Image must be under 5 MB", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const { url } = await uploader(file);
      onChange(url);
      toast({ title: `${label} uploaded` });
    } catch (e: any) {
      toast({
        title: "Upload failed",
        description: e?.response?.data?.message || "",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <p className="text-xs text-gray-500 mb-1.5">{label}</p>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        {/* Preview / empty state */}
        <div className="relative aspect-[4/3] bg-gray-50">
          {value ? (
            <img
              src={value}
              alt={label}
              className="h-full w-full object-cover"
            />
          ) : (
            <button
              type="button"
              disabled={uploading || disabled}
              onClick={() => inputRef.current?.click()}
              className="h-full w-full flex flex-col items-center justify-center gap-1.5 text-gray-400 hover:bg-gray-100 disabled:opacity-50"
            >
              <ImagePlus className="h-7 w-7" />
              <span className="text-xs">Click to upload</span>
            </button>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
            </div>
          )}
        </div>

        {/* Footer actions (only once an image exists) */}
        {value && (
          <div className="flex divide-x divide-gray-200 border-t border-gray-200">
            <button
              type="button"
              disabled={uploading || disabled}
              onClick={() => inputRef.current?.click()}
              className="flex-1 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Replace
            </button>
            <button
              type="button"
              disabled={uploading}
              onClick={() => onChange("")}
              className="flex-1 py-2 text-xs font-medium text-red-500 hover:bg-red-50 disabled:opacity-50"
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
